<ng-template #beforeTpl let-s="s">
  @if (s.before.type === 'text') {
    @if (s.before.text) {
      <tidy-text [body]="s.before.text" />
    } @else {
      <tidy-text [helper]="'Not written yet.'" />
    }
  } @else if (s.before.type === 'photo') {
    <tidy-image
      [src]="s.before.thumbnailUrl || s.before.url"
      (action)="openLightbox(s.before.url, s.after && s.after.type === 'photo' ? s.after.url : null, 'before')"
      class="lo-photo" />
  } @else {
    <tidy-text [helper]="'Not listed'" />
  }
</ng-template>

<ng-template #afterTpl let-s="s">
  @if (genError[s.id]) {
    <tidy-row>
      <tidy-text [helper]="genError[s.id]" class="lo-gen-error" />
    </tidy-row>
  }
  @if (s.afterStatus === 'pending' && canGenerate[s.id]) {
    <div class="lo-generate">
      @if (s.before.type === 'photo') {
        <tidy-text [helper]="'Photo edits can take 15–40 seconds.'" class="lo-generate-hint" />
      }
      <tidy-button
        [text]="s.before.type === 'photo' ? 'Generate photo fix' : 'Generate rewrite'"
        [action]="genActions[s.id]"
        class="secondary" />
    </div>
  } @else if (s.afterStatus === 'ready' && s.after) {
    @if (s.after.type === 'text') {
      <div class="lo-after-text">
        <tidy-text [body]="s.after.text" />
        <button type="button" class="lo-icon-btn" (click)="copy(s.after.text)" aria-label="Copy">
          <ion-icon name="copy-outline" />
        </button>
      </div>
    } @else if (s.after.type === 'photo') {
      <tidy-image
        [src]="s.after.thumbnailUrl || s.after.url"
        (action)="openLightbox(s.before.type === 'photo' ? s.before.url : s.after.url, s.after.url, 'after')"
        class="lo-photo" />
      <div class="lo-inline-actions">
        <button type="button" class="lo-icon-btn" (click)="copy(s.after.url)" aria-label="Copy image URL">
          <ion-icon name="copy-outline" />
        </button>
        <button type="button" class="lo-icon-btn" (click)="download(s.after.url, s.id + '.jpg')" aria-label="Download">
          <ion-icon name="download-outline" />
        </button>
      </div>
      @if (!historyMode) {
        <tidy-button [text]="'Have TIDY do it'" [action]="haveTidy" class="primary" />
      }
    } @else if (s.after.type === 'amenity') {
      <tidy-text [body]="('Add ' | translate) + s.after.name + (' to your amenities' | translate)" />
    }
  } @else if (historyMode) {
    <tidy-text [helper]="'Not generated for this report.'" />
  } @else {
    <tidy-text [helper]="'TIDY can take this one off your plate.'" />
    <tidy-button [text]="'Have TIDY do this'" [action]="haveTidy" class="primary" />
  }
</ng-template>

<div class="lo-report" [class.lo-report-history]="historyMode">
  @if (report) {
    <div class="lo-sticky-top">
      @if (showHeader) {
        <tidy-card class="lo-report-header-card">
          <div class="lo-header">
            <div class="lo-header-main">
              <tidy-text [header]="report.listing?.title || 'Listing Report'" />
              @if (report.listing?.location) {
                <tidy-row>
                  <tidy-text [helper]="report.listing.location" />
                </tidy-row>
              }
              @if (platformLabel) {
                <div class="lo-platform-line">
                  @if (platformIcon) {
                    <img class="lo-platform-icon" [src]="platformIcon" [alt]="platformLabel" />
                  }
                  <tidy-text [helper]="platformListingId ? platformLabel + ' · ' + platformListingId : platformLabel" />
                </div>
              }
              @if (summaryLine) {
                <tidy-row>
                  <tidy-text [body]="summaryLine" />
                </tidy-row>
              }
              @for (warning of warnings; track $index) {
                <tidy-row>
                  <tidy-text [helper]="warning" class="lo-warning" />
                </tidy-row>
              }
            </div>
          </div>
        </tidy-card>
      }

      @if (showBulkActions && !historyMode && groups.length) {
        <tidy-card class="lo-selectbar">
          <tidy-row [alignLastItemRight]="true">
            <div class="lo-selectall">
              <tidy-checkbox
                [initValue]="allSelected"
                [items]="[{ viewValue: '', value: true }]"
                (checkedChange)="toggleSelectAll()" />
              <tidy-text [body]="selectedCount > 0 ? ('Select all suggestions (' | translate) + selectedCount + (' selected)' | translate) : 'Select all suggestions'" />
            </div>
            <tidy-button
              [text]="selectedCount > 0 ? ('Have TIDY do these (' | translate) + selectedCount + ')' : 'Have TIDY do these'"
              [action]="haveTidy"
              class="primary" />
          </tidy-row>
        </tidy-card>
      }
    </div>

    @if (!hasV2Content) {
      <tidy-card class="lo-legacy-notice">
        <tidy-text [title]="'Legacy report'" />
        <tidy-text [helper]="'This report was created before suggestion cards were available. Generate a new report to see the full Before and After experience.'" />
      </tidy-card>
    }

    @for (group of groups; track group.key) {
      <div class="lo-group">
        <div class="lo-group-head">
          <tidy-image [src]="group.icon" class="lo-group-icon" />
          <tidy-text [title]="group.label" />
        </div>

        @if (group.isPhotos) {
          @for (asset of group.photoAssets; track asset.key) {
            <tidy-card class="lo-suggestion-card lo-photo-card">
              <div class="lo-asset-head">
                @if (asset.hero) {
                  <tidy-image
                    [src]="asset.hero.thumbnailUrl || asset.hero.url"
                    (action)="openLightbox(asset.hero.url, null, 'before')"
                    class="lo-hero" />
                } @else {
                  <tidy-image [src]="'assets/svg/camera-outline.svg'" class="lo-hero lo-hero-placeholder" />
                }
                <tidy-text [header]="asset.assetLabel" />
              </div>

              @for (change of asset.changes; track change.suggestion.id; let first = $first) {
                @if (!first) {
                  <tidy-row class="extra-top-padding extra-bottom-padding">
                    <tidy-divider />
                  </tidy-row>
                }
                <div class="lo-change">
                  <div class="lo-change-head">
                    @if (showBulkActions && !historyMode) {
                      <tidy-checkbox
                        [initValue]="selected[change.suggestion.id]"
                        [items]="[{ viewValue: '', value: true }]"
                        (checkedChange)="toggleSelect(change.suggestion.id)" />
                    }
                    <div class="lo-change-head-text">
                      <span class="lo-kind-chip">{{ change.kind }}</span>
                      <tidy-text [helper]="change.suggestion.why" />
                    </div>
                  </div>

                  @if (change.suggestion.afterStatus === 'ready' && change.suggestion.after) {
                    <div class="lo-grid">
                      <div class="lo-cell">
                        <tidy-text [helper]="'Before'" class="lo-col-label" />
                        @if (change.suggestion.before.type === 'text') {
                          @if (change.suggestion.before.text) {
                            <tidy-text [body]="change.suggestion.before.text" />
                          } @else {
                            <tidy-text [helper]="'Not written yet.'" />
                          }
                        } @else {
                          <tidy-text [helper]="'Current photo shown above.'" />
                        }
                      </div>
                      <div class="lo-cell lo-cell-after">
                        <tidy-text [helper]="'After'" class="lo-col-label" />
                        <ng-container *ngTemplateOutlet="afterTpl; context: { s: change.suggestion }" />
                      </div>
                    </div>
                  } @else {
                    <div class="lo-change-action">
                      <ng-container *ngTemplateOutlet="afterTpl; context: { s: change.suggestion }" />
                    </div>
                  }
                </div>
              }
            </tidy-card>
          }
        } @else {
          @for (s of group.cards; track s.id) {
            <tidy-card class="lo-suggestion-card lo-text-card">
              <div class="lo-card-head">
                @if (showBulkActions && !historyMode) {
                  <tidy-checkbox
                    [initValue]="selected[s.id]"
                    [items]="[{ viewValue: '', value: true }]"
                    (checkedChange)="toggleSelect(s.id)" />
                }
                <div class="lo-card-head-text">
                  @if (cardLabels[s.id]) {
                    <tidy-text [header]="cardLabels[s.id]" />
                  }
                  <tidy-text [helper]="s.why" />
                </div>
              </div>
              <div class="lo-grid">
                <div class="lo-cell">
                  <tidy-text [helper]="'Before'" class="lo-col-label" />
                  <ng-container *ngTemplateOutlet="beforeTpl; context: { s: s }" />
                </div>
                <div class="lo-cell lo-cell-after">
                  <tidy-text [helper]="'After'" class="lo-col-label" />
                  <ng-container *ngTemplateOutlet="afterTpl; context: { s: s }" />
                </div>
              </div>
            </tidy-card>
          }
        }
      </div>
    }

    @if (amenityNames.length) {
      <tidy-card class="lo-context-card">
        <div class="lo-group-head">
          <tidy-image [src]="'assets/svg/star-outline.svg'" class="lo-group-icon" />
          <tidy-text [title]="'Top amenities to boost revenue'" />
        </div>
        <tidy-row>
          <tidy-text [helper]="'These tend to drive the most bookings — add any you can. We only show ones not already on your listing.'" />
        </tidy-row>
        <div class="lo-pills">
          @for (name of amenityNames; track name) {
            <span class="lo-pill">{{ name }}</span>
          }
        </div>
      </tidy-card>
    }

    @if (nearby && (nearby.events?.length || nearby.restaurants?.length || nearby.transit?.length)) {
      <tidy-card class="lo-context-card">
        <div class="lo-group-head">
          <tidy-image [src]="'assets/svg/location-outline.svg'" class="lo-group-icon" />
          <tidy-text [title]="'Nearby'" />
        </div>
        @if (nearby.events?.length) {
          <tidy-row><tidy-text [header]="'Events'" /></tidy-row>
          @for (event of nearby.events | slice:0:5; track $index) {
            <tidy-row><tidy-text [body]="event.name + (event.date ? ' — ' + event.date : '')" /></tidy-row>
          }
        }
        @if (nearby.restaurants?.length) {
          <tidy-row><tidy-text [header]="'Restaurants'" /></tidy-row>
          @for (restaurant of nearby.restaurants | slice:0:5; track $index) {
            <tidy-row><tidy-text [body]="restaurant.name + (restaurant.rating ? ' — ' + restaurant.rating + ' stars' : '')" /></tidy-row>
          }
        }
        @if (nearby.transit?.length) {
          <tidy-row><tidy-text [header]="'Transit'" /></tidy-row>
          @for (transit of nearby.transit | slice:0:5; track $index) {
            <tidy-row><tidy-text [body]="transit.name + (transit.distance ? ' — ' + transit.distance : '')" /></tidy-row>
          }
        }
      </tidy-card>
    }

    @if (run) {
      <div class="lo-run-details">
        <button type="button" class="lo-details-toggle" (click)="toggleDetails()" [attr.aria-expanded]="detailsOpen">
          <tidy-text [actionBody]="detailsOpen ? 'Hide details' : 'View details'" class="link" />
          <ion-icon name="chevron-down-outline" class="lo-details-chevron" [class.is-open]="detailsOpen" />
        </button>
        @if (detailsOpen) {
          <div class="lo-details-grid">
            @for (row of runDetailRows; track row.label) {
              <div>
                <span class="lo-detail-label">{{ row.label }}</span>
                <span class="lo-detail-value">{{ row.value }}</span>
              </div>
            }
          </div>
          @if (runFailureMessage) {
            <div class="lo-details-error">{{ runFailureMessage }}</div>
          }
          @if (runAppliedChanges.length > 0) {
            <div class="lo-details-changes">
              @for (change of runAppliedChanges; track change.field) {
                <div class="lo-details-change">
                  <div class="lo-details-change-field">{{ change.field }}</div>
                  <div class="lo-details-change-columns">
                    <div>
                      <span class="lo-detail-label">Before</span>
                      <p>{{ change.before }}</p>
                    </div>
                    <div>
                      <span class="lo-detail-label">After</span>
                      <p>{{ change.after }}</p>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        }
      </div>
    }

    @if (lightbox) {
      <div class="lo-lightbox" (click)="closeLightbox()">
        <div class="lo-lightbox-inner" (click)="$event.stopPropagation()">
          @if (lightbox.enhancedUrl) {
            <div class="lo-lightbox-toggle">
              <tidy-text [actionBody]="'Before'" (action)="setLightboxView('before')" [class]="lightbox.view === 'before' ? 'link lo-lightbox-tab active' : 'link lo-lightbox-tab'" />
              <tidy-text [actionBody]="'After'" (action)="setLightboxView('after')" [class]="lightbox.view === 'after' ? 'link lo-lightbox-tab active' : 'link lo-lightbox-tab'" />
            </div>
          }
          <img
            class="lo-lightbox-img"
            [src]="lightbox.view === 'after' && lightbox.enhancedUrl ? lightbox.enhancedUrl : lightbox.originalUrl" />
        </div>
        <tidy-text [actionBody]="'Close'" (action)="closeLightbox()" class="link lo-lightbox-close" />
      </div>
    }
  }
</div>
